import {makeAutoObservable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {ISquadsStore} from "data/stores/squads/squads.store";
import {Bindings} from "data/constants/bindings";
import type {IHeavyPredictionsStore} from "data/stores/heavy_predictions/heavy_predictions.store";
import {eq, isEmpty, take} from "lodash";
import type {IStandingsStore, IUserStanding} from "data/stores/standings/standings.store";
import type {IConferencesStore} from "data/stores/conferences/conferences.store";
import {TutorialType} from "data/enums";
import type {ITutorialStore} from "data/stores/tutorial/tutorial.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IOrderedStandingsStore} from "data/stores/ordered_standings/ordered_standings.store";

export interface IHPPlayoffStandingsController extends ViewController {
	readonly i18n: ILocalizationStore;

	get isRealStandings(): boolean;
	get userNfcStandings(): IUserStanding[];
	get userAfcStandings(): IUserStanding[];

	openTutorialHandler: () => void;
}

@injectable()
export class HPPlayoffStandingsController implements IHPPlayoffStandingsController {
	get isRealStandings() {
		return isEmpty(this._hpStore.predictionBySelectedRoundId);
	}

	get userNfcStandings() {
		return this.createStandingArr(
			this._standingsStore.userStandings,
			this._conferenceStore.nfcConferenceId
		);
	}

	get userAfcStandings() {
		return this.createStandingArr(
			this._standingsStore.userStandings,
			this._conferenceStore.afcConferenceId
		);
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore,
		@inject(Bindings.StandingsStore) private _standingsStore: IStandingsStore,
		@inject(Bindings.ConferencesStore) private _conferenceStore: IConferencesStore,
		@inject(Bindings.HeavyPredictionsStore) private _hpStore: IHeavyPredictionsStore,
		@inject(Bindings.TutorialStore) private _tutorialStore: ITutorialStore,
		@inject(Bindings.OrderedStandingsStore) private _orderStandingsStore: IOrderedStandingsStore
	) {
		makeAutoObservable(this);
	}

	private createStandingArr = (standings: IUserStanding[], conferenceId: number) => {
		const standingByRound = standings.filter((it) => {
			if (it.roundId === this._roundStore.actualStandingsRoundId) {
				const squad = this._squadsStore.getSquadById(it.squadId);
				return eq(squad?.conferenceId, conferenceId);
			}
			return false;
		});

		return take(this._orderStandingsStore.getSortedStandings(standingByRound, conferenceId), 7);
	};

	openTutorialHandler = () => {
		this._tutorialStore.openTutorial(TutorialType.HEAVE_PREDICTOR);
	};
}
